import React, { createContext, useContext, useState, useEffect } from "react";

export type SavingGoal = {
  id: string;
  category: string;
  amount: number;
  title?: string;
};

type SavingGoalsContextType = {
  savingGoals: SavingGoal[];
  addGoal: (goal: Omit<SavingGoal, "id">) => void;
  removeGoal: (id: string) => void;
};

const SavingGoalsContext = createContext<SavingGoalsContextType | undefined>(
  undefined
);

export const SavingGoalsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [savingGoals, setSavingGoals] = useState<SavingGoal[]>(() => {
    const stored = localStorage.getItem("savingGoals");
    return stored ? JSON.parse(stored) : [];
  });

  useEffect(() => {
    localStorage.setItem("savingGoals", JSON.stringify(savingGoals));
  }, [savingGoals]);

  const addGoal = (goal: Omit<SavingGoal, "id">) => {
    const newGoal = { ...goal, id: crypto.randomUUID() };
    setSavingGoals((prev) => [...prev, newGoal]);
  };

  const removeGoal = (id: string) => {
    setSavingGoals((prev) => prev.filter((g) => g.id !== id));
  };

  return (
    <SavingGoalsContext.Provider value={{ savingGoals, addGoal, removeGoal }}>
      {children}
    </SavingGoalsContext.Provider>
  );
};

export const useSavingGoals = () => {
  const context = useContext(SavingGoalsContext);
  if (!context) {
    throw new Error("useSavingGoals must be used within a SavingGoalsProvider");
  }
  return context;
};
